import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreditableCompanies } from '../api/company-credit';
import { useReassignCompany } from '../api/users';

/** Pounds, from the pence the API returns. */
function pounds(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/**
 * Moves an approved user to a different firm.
 *
 * The repair for an approval that attached somebody to the wrong company. It is
 * deliberately more than a dropdown: membership is financial access — the
 * destination's pooled balance, its whole ledger including colleagues' spending,
 * and the right to edit its registered details — so the dialog names the
 * destination's balance and requires a reason, exactly as the approval modal
 * does for the same decision made the first time.
 *
 * The list comes from the creditable-companies endpoint, which is already the
 * "pick a firm" list and already returns the balance and user count this needs.
 * There is no create-a-company path here on purpose: a picker that can also
 * create is a picker that makes duplicates while clearing them up.
 */
export function ReassignCompanyDialog({
  userId,
  userName,
  currentCompanyId,
  currentCompanyName,
}: {
  userId: string;
  userName: string;
  currentCompanyId: string | null;
  currentCompanyName: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // Only fetched once the dialog is open: this is an admin:billing endpoint and
  // most visits to a user record never reassign anybody.
  const companies = useCreditableCompanies(open);
  const reassign = useReassignCompany();

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = companies.data ?? [];
    // The firm they are already in is not a destination.
    const eligible = all.filter((company) => company.id !== currentCompanyId);
    if (!term) return eligible;
    return eligible.filter((company) => company.name.toLowerCase().includes(term));
  }, [companies.data, currentCompanyId, search]);

  const selected = matches.find((company) => company.id === selectedId) ?? null;

  function openDialog() {
    setSearch('');
    setSelectedId(null);
    setReason('');
    setOpen(true);
  }

  function handleMove() {
    if (!selected) return;

    reassign.mutate(
      {
        id: userId,
        companyId: selected.id,
        // What this screen showed. The server re-reads inside the transaction
        // and refuses if the user has moved since, rather than silently
        // overwriting a change the administrator never saw.
        expectedCompanyId: currentCompanyId,
        reason: reason.trim(),
      },
      // Closes only on success — a refusal must not look like it worked.
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={openDialog}>
        Move company
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Move {userName} to another company</DialogTitle>
            <DialogDescription>
              Their assessments stay with them. Their access to{' '}
              {currentCompanyName ?? 'their current company'}&rsquo;s balance ends, and they gain
              access to the new company&rsquo;s.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reassign-search">Company</Label>
              <Input
                id="reassign-search"
                placeholder="Search by name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            {companies.isLoading && (
              <p className="text-muted-foreground text-sm">Loading companies…</p>
            )}

            {companies.isError && (
              <p className="text-destructive text-sm">
                The company list could not be loaded. This needs the billing permission.
              </p>
            )}

            {!companies.isLoading && !companies.isError && matches.length === 0 && (
              <p className="text-muted-foreground text-sm">No other company matches.</p>
            )}

            {matches.length > 0 && (
              // A list of buttons rather than a Select: the balance and user
              // count are the consequence being agreed to, and a native option
              // cannot show them.
              <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-1">
                {matches.map((company) => (
                  <li key={company.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(company.id)}
                      aria-pressed={selectedId === company.id}
                      className={`flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm ${
                        selectedId === company.id ? 'bg-muted font-medium' : 'hover:bg-muted/60'
                      }`}
                    >
                      <span>{company.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {pounds(company.balancePence)} ·{' '}
                        {company.userCount === 1 ? '1 user' : `${company.userCount} users`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {selected && (
              <p className="rounded-md bg-muted p-3 text-sm">
                Moving <strong>{userName}</strong> to <strong>{selected.name}</strong> gives them
                access to its balance of <strong>{pounds(selected.balancePence)}</strong> and its
                transaction history. Their own assessments are unaffected.
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="reassign-reason">Reason</Label>
              <Textarea
                id="reassign-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              disabled={!selected || reason.trim().length === 0 || reassign.isPending}
              onClick={handleMove}
            >
              {reassign.isPending ? 'Moving…' : 'Move company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
